import Icon1 from "@assets/icons/Icon";
import { UserAvatar } from "@components/ui/UserAvatar";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import SideBarButton from "./SideBarButton";
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { navigateTo } from "context/navigation.service";
import { useDataStore } from "@store/user_org";
import { useAuthStore } from "@store/auth";
import DropButton from "@components/ui/buttons/DropButton";
import SideMenuButton from "./SideMenuButton";
import { colors } from "sexy";
import { useApiConfigStore } from "@store/api_config";
import CheckBox from "@components/ui/selection/CheckBox";
import { CreateProjectModal } from "@components/modals/CreateProjectModal";
import { CreateBroadcastModal } from "@components/modals/CreateBroadcastModal";
import { CreatePartnerModal } from "@components/modals/CreatePartnerModal";
import { isGUEST, isDESSINATEUR } from "@helpers/accessHelpers";
import { isOwnerOrAdmin } from "@utils/permissions";
import firebase from "@services/firebase";
import { doc, onSnapshot } from "firebase/firestore";
import {
  activateMaintenance as apiActivate,
  deactivateMaintenance as apiDeactivate,
} from "@services/api/maintenance";
import { showNotification } from "context/notification.service";

const DEV_LOGIN_AS_UID = "gV0ivsgk61YLbW5G31mJXZTXVWW2";

export default function SideBar() {
  const [settings_open, setSettingsOpen] = useState<any>(false);
  const [isVisible, setIsVisible] = useState(false);
  const userData = useDataStore((state) => state.userData);
  const orgData = useDataStore((state) => state.orgData);
  const orgMembers = useDataStore((state) => state.orgMembers);
  const clearAllData = useDataStore((state) => state.clearAllData);
  const logout = useAuthStore((state) => state.logout);
  const loginAs = useAuthStore((state) => state.loginAs);
  const isImpersonating = useAuthStore((state) => state.isImpersonating);
  const returnToAdmin = useAuthStore((state) => state.returnToAdmin);
  const isGuest = isGUEST(userData);
  const isDessinateur = isDESSINATEUR(userData);
  const [loginAsLoading, setLoginAsLoading] = useState(false);

  // API Config
  const isDev = useApiConfigStore((state) => state.isDev);
  const devApiUrl = useApiConfigStore((state) => state.devApiUrl);
  const setIsDev = useApiConfigStore((state) => state.setIsDev);
  const setDevApiUrl = useApiConfigStore((state) => state.setDevApiUrl);

  // Create modals
  const [showCreateProject, setShowCreateProject] = useState(false);
  const [showCreateBroadcast, setShowCreateBroadcast] = useState(false);
  const [showCreatePartner, setShowCreatePartner] = useState(false);

  // Only show dev toggle for specific org
  const isDevOrg = orgData?.id === "ur1LXurQw1Fv3KBy8XDI";

  // Maintenance
  const [maintenanceActive, setMaintenanceActive] = useState(false);
  const [maintenanceMessage, setMaintenanceMessage] = useState("");
  const [maintenanceEnd, setMaintenanceEnd] = useState("");

  useEffect(() => {
    if (!isDevOrg) return;
    const statusRef = doc(firebase.db, "maintenance", "status");
    const unsub = onSnapshot(statusRef, (snap) => {
      if (!snap.exists()) {
        setMaintenanceActive(false);
        return;
      }
      const data = snap.data();
      setMaintenanceActive(data.active === true);
      setMaintenanceMessage(data.message || "");
      setMaintenanceEnd(data.estimated_end || "");
    });
    return () => unsub();
  }, [isDevOrg]);

  const toggleMaintenance = async (checked: boolean) => {
    if (checked) {
      setMaintenanceActive(true);
    } else {
      try {
        await apiDeactivate();
      } catch (error) {
        console.error("Failed to deactivate maintenance:", error);
      }
    }
  };

  const activateMaintenance = async () => {
    try {
      await apiActivate({
        message: maintenanceMessage,
        estimated_end: maintenanceEnd,
      });
    } catch (error) {
      console.error("Failed to activate maintenance:", error);
    }
  };

  const handleDevLoginAs = async () => {
    setLoginAsLoading(true);
    try {
      if (isImpersonating) {
        clearAllData();
        await returnToAdmin();
        showNotification({ type: "success", message: "Retour au compte admin" });
      } else {
        clearAllData();
        await loginAs(DEV_LOGIN_AS_UID);
        showNotification({ type: "success", message: "Connexion effectuée" });
      }
    } catch (error) {
      console.error("Dev login-as failed:", error);
      showNotification({ type: "error", message: "Impossible de se connecter" });
    } finally {
      setLoginAsLoading(false);
    }
  };

  // Debug logging
  useEffect(() => {
    console.log("🔍 SideBar - userData:", userData);
    console.log("🔍 SideBar - orgData:", orgData);
    console.log("🔍 SideBar - isDevOrg:", isDevOrg);
  }, [userData, orgData, isDevOrg]);

  // Subscribe to React Router's location so the active tab highlight is
  // always in sync with the real route. The previous implementation
  // monkey-patched window.history.pushState and read window.location.pathname
  // manually, which could desync (nested overrides across re-mounts, idle
  // → remount race) and leave the sidebar showing a different page than
  // the one actually rendered.
  const location = useLocation();
  const actual_page = location.pathname;

  useEffect(() => {
    if (settings_open) {
      setIsVisible(true);
    } else {
      setIsVisible(false);
    }
  }, [settings_open]);

  return (
    <div className="w-72 max-w-72 min-w-72 bg-white h-screen justify-start pl-2 border-r-1 flex flex-col">
      {settings_open && (
        <div
          onClick={() => setSettingsOpen(false)}
          className="fixed h-screen w-screen top-0 left-0 z-50 flex justify-center items-center"
        >
          <div
            className={`absolute bottom-4 z-40 w-200 flex flex-col ${
              isVisible ? "scale-100 opacity-100 " : "scale-90 opacity-0"
            } duration-75 transition-all h-fit text-shadow_50 border-1 border-shadow_10 bg-white shadow-xl rounded-lg`}
            style={{ left: "230px" }}
          >
            <div className="flex flex-row gap-2 items-center border-b-1 p-3">
              <div className="h-8 w-8 rounded-lg flex items-center justify-center text-white overflow-hidden">
                <UserAvatar
                  src={userData?.customization.photo_url}
                  className="h-10 w-10 rounded-lg"
                  value={
                    userData?.customization.avatar_seed ||
                    userData?.infos.display_name
                  }
                  size={40}
                />
              </div>
              <div className="flex flex-col">
                <div className="text-h3 font-semibold text-black ">
                  {userData?.infos.display_name}
                </div>
                <div className="text-h4">
                  {userData?.infos.email.substring(0, 15)}
                </div>
              </div>
            </div>

            <div className="flex flex-col p-1">
              <SideMenuButton
                label="Compte"
                name="user"
                color={colors.neutral_400}
                onClick={() => {
                  navigateTo({ route: "/settings?page=account" });
                  setSettingsOpen(false);
                }}
              />
              {!isGuest && (
                <SideMenuButton
                  label="Entreprise"
                  name="business"
                  color={colors.neutral_400}
                  onClick={() => {
                    navigateTo({ route: "/settings?page=company" });
                    setSettingsOpen(false);
                  }}
                />
              )}
              <SideMenuButton
                label="Paramètres"
                name="settings"
                color={colors.neutral_400}
                onClick={() => {
                  navigateTo({ route: "/settings" });
                  setSettingsOpen(false);
                }}
              />
            </div>

            <div className="flex flex-col p-1 border-t-1">
              <SideMenuButton
                label="Déconnexion"
                color={colors.neutral_400}
                name="logout"
                onClick={() => {
                  logout();
                }}
              />
            </div>
          </div>
        </div>
      )}
      <div className="flex flex-col pl-2 mb-auto">
        <img
          src={require("@assets/photos/mbmo.png")}
          className="w-24 h-auto mt-6 object-contain mb-8"
        />
        {!isGuest && (
          <DropButton
            startIcon={<Icon1 name="plus" size={16} color="black" />}
            label="Nouveau"
          >
            <SideBarButton
              label="Projet"
              name="cube"
              onClick={() => setShowCreateProject(true)}
            />
            <SideBarButton
              label="Annonce"
              name="broadcast"
              onClick={() => setShowCreateBroadcast(true)}
            />
            <SideBarButton
              label="Artisan"
              name="expert"
              onClick={() => setShowCreatePartner(true)}
            />
          </DropButton>
        )}
        <div className="flex flex-col mt-6 mr-6">
          {!isGuest && (
            <SideBarButton
              onClick={() => navigateTo({ route: "/home" })}
              label="Accueil"
              name="dashboard"
              highlighted={actual_page.includes("/home")}
            />
          )}
          <SideBarButton
            highlighted={actual_page.includes("/project")}
            onClick={() => navigateTo({ route: "/projects" })}
            label="Projets"
            name="cube"
          />
          {isGuest && (
            <SideBarButton
              onClick={() => navigateTo({ route: "/bibliotheque" })}
              label="Bibliothèque"
              name="folder"
              highlighted={actual_page.includes("/bibliotheque")}
            />
          )}
          {!isGuest && (
            <SideBarButton
              onClick={() => navigateTo({ route: "/broadcasts" })}
              label="Annonces"
              name="broadcast"
              highlighted={actual_page.includes("/broadcast")}
            />
          )}
          {!isGuest && !isDessinateur && (
            <SideBarButton
              onClick={() => navigateTo({ route: "/partners" })}
              label="Artisans"
              name="expert"
              highlighted={actual_page.includes("/partners")}
            />
          )}
          {!isGuest && !isDessinateur && (
            <SideBarButton
              onClick={() => navigateTo({ route: "/facturation" })}
              label="Facturation"
              name="facturation"
              highlighted={actual_page.includes("/facturation")}
            />
          )}
          {isOwnerOrAdmin(userData) && (
            <SideBarButton
              onClick={() => navigateTo({ route: "/suivi-salaries" })}
              label="Suivi salariés"
              name="users"
              highlighted={actual_page.includes("/suivi-salaries")}
            />
          )}
          <SideBarButton
            onClick={() => navigateTo({ route: "/settings?page=account" })}
            label="Paramètres"
            name="settings"
            highlighted={actual_page.includes("/settings")}
          />
        </div>
      </div>

      {userData?.config?.admin && (
        <div className=" flex flex-row mx-2 mb-2 gap-2 items-center">
          <CheckBox
            checked={isDev}
            onChange={(e) => setIsDev(e.target.checked)}
          ></CheckBox>
          <span className="text-h3 text-neutral-600 flex-shrink-0">Local</span>
          {isDev && (
            <input
              type="text"
              value={devApiUrl}
              onChange={(e) => setDevApiUrl(e.target.value)}
              placeholder="localhost:8000"
              className="text-h5 px-2 py-1 border-1 border-neutral-300 rounded flex-1 min-w-0"
            />
          )}
        </div>
      )}

      {userData?.config?.admin && isDevOrg && (
        <div className="mx-2 mb-2">
          <SecondaryButton
            variant="small"
            label={
              loginAsLoading
                ? "Connexion..."
                : isImpersonating
                  ? "Retour admin"
                  : "Login as"
            }
            disabled={loginAsLoading}
            onClick={handleDevLoginAs}
          />
        </div>
      )}

      {userData?.config?.admin && isDevOrg && (
        <div className="flex flex-col mx-2 mb-2 gap-2">
          <div className="flex flex-row gap-2 items-center">
            <CheckBox
              checked={maintenanceActive}
              onChange={(e) => toggleMaintenance(e.target.checked)}
            />
            <span className="text-h3 text-neutral-600 flex-shrink-0">
              Maintenance
            </span>
          </div>
          {maintenanceActive && (
            <div className="flex flex-col gap-2 pl-7">
              <input
                type="text"
                value={maintenanceMessage}
                onChange={(e) => setMaintenanceMessage(e.target.value)}
                placeholder="Message..."
                className="text-h5 px-2 py-1 border-1 border-neutral-300 rounded"
              />
              <input
                type="datetime-local"
                value={maintenanceEnd}
                onChange={(e) => setMaintenanceEnd(e.target.value)}
                className="text-h5 px-2 py-1 border-1 border-neutral-300 rounded"
              />
              <button
                onClick={activateMaintenance}
                className="text-h5 px-3 py-1 bg-black text-white rounded hover:bg-neutral-800 transition-colors"
              >
                Activer
              </button>
            </div>
          )}
        </div>
      )}

      {!isGuest &&
        orgMembers.filter(
          (u) => u.config?.access_type !== "GUEST" && u.status !== "inactive",
        ).length > 0 && (
          <div
            onClick={() => navigateTo({ route: "/settings?page=team" })}
            className="flex  cursor-pointer hover:bg-neutral-100 rounded-xl p-2 flex-wrap items-center m-2 mr-4"
          >
            {orgMembers
              .filter(
                (u) =>
                  u.config?.access_type !== "GUEST" && u.status !== "inactive",
              )
              .sort((a, b) =>
                a.activity?.connected === b.activity?.connected
                  ? 0
                  : a.activity?.connected
                    ? -1
                    : 1,
              )
              .slice(0, 8)
              .map((user, index) => (
                <div
                  style={{
                    zIndex: 0,
                    marginLeft: index === 0 ? 0 : -index * 3,
                    opacity: 2 - index * 0.3,
                  }}
                  key={index}
                  className="min-w-8 h-8 rounded-full relative"
                >
                  <UserAvatar
                    className="w-8 h-8 rounded-full "
                    src={user.customization?.photo_url}
                    value={
                      user.customization?.avatar_seed ||
                      user.infos?.display_name
                    }
                    size={32}
                  />
                  {user.activity?.connected && (
                    <div className="absolute bottom-0 right-0 w-2 h-2 rounded-full ring-2 ring-emerald-100 bg-emerald-300" />
                  )}
                </div>
              ))}
          </div>
        )}

      {userData && (
        <div
          onClick={() => setSettingsOpen(true)}
          className="flex flex-row select-none  mr-4  cursor-pointer max-w-full justify-between p-2 m-2 mb-4 items-center rounded-xl hover:bg-neutral-100"
        >
          <div className="flex flex-row gap-3 max-w-full items-center">
            <div className="min-w-10 h-10 rounded-full overflow-hidden">
              <UserAvatar
                className="w-10 h-10 rounded-full"
                src={userData.customization.photo_url}
                value={
                  userData.customization.avatar_seed ||
                  userData.infos.display_name
                }
                size={40}
              />
            </div>
            <div className="flex flex-col flex-grow max-w-full">
              <div className="text-h3 text-nowrap text-black text-ellipsis overflow-hidden select-none max-w-24">
                {userData.infos.display_name}
              </div>
              <div className="text-h6 text-shadow_50 max-h-4 text-ellipsis overflow-hidden select-none max-w-24">
                {userData.infos.email}
              </div>
            </div>
          </div>
          <div className="items-center flex flex-col">
            <Icon1 name="menu" size={25} color="grey" />
          </div>
        </div>
      )}
      {showCreateProject && (
        <CreateProjectModal onClose={() => setShowCreateProject(false)} />
      )}
      {showCreateBroadcast && (
        <CreateBroadcastModal onClose={() => setShowCreateBroadcast(false)} />
      )}
      {showCreatePartner && (
        <CreatePartnerModal onClose={() => setShowCreatePartner(false)} />
      )}
    </div>
  );
}
